import React from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Mail } from 'lucide-react';
import { productData } from '../../../data/mockData';

/**
 * Step 5 — Quantity picker. Structure depends on the product's
 * `quantityType` config (page / roll / individual) plus a custom-qty input.
 * Some subcategories short-circuit to a "Custom Quote" CTA here instead
 * of surfacing a picker at all.
 */
export const Step5Quantity = ({
  requiresCustomQuoteAtQuantity,
  selectedProduct,
  selectedFormat,
  width,
  height,
  unit,
  getQuantityConfig,
  calculateItemsPerPage,
  quantity,
  setQuantity,
  customQuantity,
  setCustomQuantity,
  calculatePrice,
  formatCurrency,
  // Compact mode — used by the Page-2 redesign. Collapses spacing,
  // shrinks the tier table, drops the blue banner, and tucks the
  // Custom-Quantity input INSIDE the "Select Quantity" label row
  // (instead of below the table) so customers can tweak quantity in
  // one uninterrupted visual zone.
  compact = false,
}) => {
  if (requiresCustomQuoteAtQuantity()) {
    return (
      <div className="space-y-4">
        <div className="text-center py-12">
          <div className="mb-6 animate-fade-in">
            <h3 className="text-3xl font-bold text-[#4A7C59] mb-4">Custom Quote Required</h3>
            <p className="text-gray-600 text-lg mb-2">
              This product requires a custom quote for quantity and pricing.
            </p>
            <p className="text-gray-600">
              Click below to request a personalized quote from our team.
            </p>
          </div>
          <Button
            onClick={() => {
              const category = productData.categories.find((c) => c.id === selectedProduct);
              const subcategory = category?.subcategories.find((s) => s.id === selectedFormat);
              const subject = `Custom Quote Request - ${subcategory?.name}`;
              const body = `Hello,

I would like to request a custom quote for:

PRODUCT:
- Category: ${category?.name}
- Product Type: ${subcategory?.name}
- Dimensions: ${width} x ${height} ${unit}

Please contact me to discuss specifications, pricing, and timeline.

Best regards`;
              window.location.href = `mailto:jfishburn@eg3.ca?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
            }}
            className="bg-gradient-to-r from-[#6FBF73] to-[#4A7C59] hover:from-[#4A7C59] hover:to-[#6FBF73] text-white text-lg px-10 py-7 h-auto shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 hover:-translate-y-1"
          >
            <Mail className="w-6 h-6 mr-2" />
            GET A CUSTOM QUOTE
          </Button>
          <p className="text-sm text-gray-500 mt-4">
            An email will be created to an EG3 Product Expert with your request
          </p>
        </div>
      </div>
    );
  }

  const config = getQuantityConfig();
  const quantityType = config?.quantityType || 'individual';
  const minQty = config?.minQty || 1;
  const maxQty = config?.maxQty || 10000;
  const unitLabel = config?.unitLabel || 'Item';

  const itemsPerPage = quantityType === 'page' ? calculateItemsPerPage(width, height) : null;

  // For Page products, the XLSX pricing tiers (1/5/10/25/...) are PAGE
  // counts, not sticker counts — one "1 Page" row in the catalog is a
  // flat fee regardless of how many stickers fit on that page. We track
  // both values per option so the UI can show "{stickers} Stickers
  // ({pages} Pages)" while the price math below drives off `pages`
  // directly. See pricing_table.json → `Labels|Page (...)` rows.
  let quantityOptions = [];
  if (quantityType === 'page') {
    const pageOptions = [1, 2, 5, 10, 25, 50, 100].filter((p) => p >= minQty && p <= maxQty);
    quantityOptions = pageOptions.map((pages) => ({
      pages,
      totalItems: pages * itemsPerPage,
      // `pricingQty` is what gets sent to calculatePrice. For Page
      // products this is the PAGE count, not the sticker count — the
      // backend `/api/pricing/calculate` row for this combo is priced
      // per-page by design.
      pricingQty: pages,
      label: `${pages * itemsPerPage} Sticker${pages * itemsPerPage !== 1 ? 's' : ''} (${pages} Page${pages > 1 ? 's' : ''})`,
    }));
  } else if (quantityType === 'roll') {
    const rollOptions = [250, 500, 1000, 2500, 5000, 10000, 20000, 40000].filter(
      (q) => q >= minQty && q <= maxQty
    );
    quantityOptions = rollOptions.map((qty) => ({
      qty,
      pricingQty: qty,
      label: `${qty.toLocaleString()} ${unitLabel}s`,
    }));
  } else {
    let baseOptions = [];
    if (minQty === 1 && maxQty >= 20000) {
      baseOptions = [1, 5, 10, 25, 50, 100, 250, 500, 1000, 2000, 5000, 10000, 20000];
    } else if (minQty === 25 && maxQty === 2000) {
      baseOptions = [25, 50, 100, 250, 500, 1000, 2000];
    } else if (minQty === 10 && maxQty === 1000) {
      baseOptions = [10, 25, 50, 100, 250, 500, 1000];
    } else if (maxQty === 500) {
      baseOptions = [1, 5, 10, 25, 50, 100, 250, 500];
    } else if (maxQty === 250) {
      baseOptions = [1, 5, 10, 25, 50, 100, 250];
    } else if (maxQty === 50) {
      baseOptions = [1, 5, 10, 25, 50];
    } else {
      baseOptions = [minQty, minQty * 2, minQty * 5, minQty * 10].filter((q) => q <= maxQty);
    }
    quantityOptions = baseOptions
      .filter((q) => q >= minQty && q <= maxQty)
      .map((qty) => ({
        qty,
        pricingQty: qty,
        label: `${qty.toLocaleString()} ${unitLabel}${qty > 1 ? 's' : ''}`,
      }));
  }

  const handleApplyCustomQty = () => {
    const qty = parseInt(customQuantity);
    if (customQuantity && !isNaN(qty) && qty >= minQty && qty <= maxQty) {
      setQuantity(qty);
      setCustomQuantity('');
    } else if (customQuantity) {
      alert(
        `Please enter a valid quantity between ${minQty.toLocaleString()} and ${maxQty.toLocaleString()} ${unitLabel}s`
      );
    }
  };

  return (
    <div className={compact ? 'space-y-2' : 'space-y-4'}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <Label className={compact ? 'text-sm font-bold uppercase tracking-wide text-gray-700 mb-0 block' : 'text-lg font-semibold mb-4 block'}>
          Select Quantity
          {quantityType === 'page' && itemsPerPage && (
            <span className={compact ? 'block text-[11px] font-normal text-gray-500 mt-0.5 normal-case tracking-normal' : 'text-sm text-gray-600 ml-2'}>
              ({itemsPerPage} Sticker{itemsPerPage !== 1 ? 's' : ''} per page at {width}&quot; x {height}&quot;)
            </span>
          )}
        </Label>

        {/* Inline custom quantity — lives in the same header row as the
             "Select Quantity" label so users can tweak the exact number
             without hunting for a separate form below. ALWAYS rendered
             (compact + non-compact) since the standalone block below
             would be a duplicate. */}
        <div className="flex items-center gap-1.5" data-testid="inline-custom-qty">
          <Input
            id="custom-qty-inline"
            data-testid="quote-custom-quantity-input"
            type="number"
            placeholder={`Custom (${minQty.toLocaleString()}–${maxQty.toLocaleString()})`}
            value={customQuantity}
            onChange={(e) => setCustomQuantity(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') handleApplyCustomQty();
            }}
            className="h-9 w-48 text-sm"
            min={minQty}
            max={maxQty}
          />
          <Button
            onClick={handleApplyCustomQty}
            disabled={!customQuantity || isNaN(parseInt(customQuantity))}
            data-testid="quote-custom-quantity-apply-btn"
            className="h-9 px-3 text-xs bg-gradient-to-r from-[#6FBF73] to-[#4A7C59] hover:from-[#4A7C59] hover:to-[#6FBF73] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Apply
          </Button>
        </div>
      </div>

      <div className={`bg-white rounded-lg border overflow-hidden shadow-sm ${compact ? 'max-h-64 overflow-y-auto' : ''}`}>
        <table className="w-full">
          <thead className="bg-[#4A7C59] text-white">
            <tr>
              <th className="p-3 text-left">Quantity</th>
              <th className="p-3 text-right">Price</th>
            </tr>
          </thead>
          <tbody>
            {/* Custom-quantity selection lands at the TOP of the table so
                the user's just-entered price isn't buried below seven
                tier rows. Renders only when the current quantity is NOT
                an exact catalog tier. */}
            {quantity &&
              !quantityOptions.find((opt) => opt.pricingQty === quantity) && (
                <tr
                  className="bg-green-100 border-l-4 border-[#6FBF73] sticky top-0 z-10"
                  data-testid="custom-quantity-row"
                >
                  <td className={compact ? 'p-2' : 'p-4'}>
                    <div className={`font-semibold text-[#2E5A3D] flex items-center gap-2 ${compact ? 'text-sm' : ''}`}>
                      <span className="inline-block w-2 h-2 bg-[#6FBF73] rounded-full animate-pulse"></span>
                      {quantityType === 'page' && itemsPerPage
                        ? `${(quantity * itemsPerPage).toLocaleString()} Sticker${quantity * itemsPerPage !== 1 ? 's' : ''} (${quantity} Page${quantity > 1 ? 's' : ''}) (Custom)`
                        : `${quantity.toLocaleString()} ${unitLabel}${quantity > 1 ? 's' : ''} (Custom)`}
                    </div>
                  </td>
                  <td className={`text-right font-bold text-[#2E5A3D] ${compact ? 'p-2 text-sm' : 'p-4'}`}>
                    {formatCurrency(calculatePrice(quantity))}
                  </td>
                </tr>
              )}

            {quantityOptions.map((option) => {
              // For Page products `pricingQty` is the page count (drives
              // the XLSX price lookup); the display `label` already shows
              // total stickers + pages. Selection state uses pricingQty
              // so clicking the row reflects what the backend receives.
              const qty = option.pricingQty;
              const isSelected = quantity === qty;
              return (
                <tr
                  key={`${quantityType}-${qty}`}
                  className={`cursor-pointer transition-all duration-200 ${
                    isSelected
                      ? 'bg-green-50 border-l-4 border-[#6FBF73]'
                      : 'hover:bg-gray-50 border-b hover:bg-green-50/30'
                  }`}
                  onClick={() => setQuantity(qty)}
                >
                  <td className={compact ? 'p-2' : 'p-4'}>
                    <div className={`font-semibold ${compact ? 'text-sm' : ''} ${isSelected ? 'text-[#6FBF73]' : 'text-[#4A7C59]'}`}>
                      {option.label}
                    </div>
                  </td>
                  <td className={`text-right font-semibold text-[#4A7C59] ${compact ? 'p-2 text-sm' : 'p-4'}`}>
                    {formatCurrency(calculatePrice(qty))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Volume-discount upsell hint — fires when the custom quantity
          sits strictly between two catalog tiers. Shows the "savings
          unlocked" at the next tier along with the per-unit rate there,
          which is a proven 3-8% cart-size lift for tiered-pricing
          print shops. Only rendered when:
            (a) a custom quantity is selected (not an exact tier)
            (b) a next tier exists above `quantity`
            (c) the per-unit rate at that next tier is meaningfully lower
                than the current rate (≥5% drop) so we don't spam the
                nudge on mid-tier caps where step-pricing has already
                capped at the next tier's flat price. */}
      {(() => {
        const currentInOptions = quantityOptions.find((opt) => opt.pricingQty === quantity);
        if (!quantity || currentInOptions) return null;
        const nextOption = quantityOptions.find((opt) => opt.pricingQty > quantity);
        if (!nextOption) return null;
        const currentTotal = calculatePrice(quantity);
        const nextTotal = calculatePrice(nextOption.pricingQty);
        if (!currentTotal || !nextTotal) return null;
        const currentRate = currentTotal / quantity;
        const nextRate = nextTotal / nextOption.pricingQty;
        if (nextRate >= currentRate * 0.95) return null; // skip weak / flat tiers
        const diff = nextOption.pricingQty - quantity;
        const unitNoun =
          quantityType === 'page'
            ? `Page${diff > 1 ? 's' : ''}`
            : `${unitLabel}${diff > 1 ? 's' : ''}`;
        const nextLabelNoun =
          quantityType === 'page'
            ? `${nextOption.pricingQty} Page${nextOption.pricingQty > 1 ? 's' : ''}`
            : `${nextOption.pricingQty.toLocaleString()} ${unitLabel}${nextOption.pricingQty > 1 ? 's' : ''}`;
        return (
          <div
            className="mt-3 rounded-lg border-2 border-[#4A7C59]/40 bg-gradient-to-r from-[#4A7C59]/10 to-emerald-50 px-4 py-3 flex items-start gap-3"
            data-testid="volume-discount-hint"
          >
            <span className="text-xl leading-none mt-0.5">💡</span>
            <div className="flex-1 text-sm">
              <span className="font-semibold text-[#2E5A3D]">
                Volume discount unlocked at {nextLabelNoun}
              </span>
              <span className="text-[#3B5F46] ml-1">
                — add <strong>{diff} more {unitNoun}</strong> to drop from{' '}
                <strong>{formatCurrency(currentRate)}/{quantityType === 'page' ? 'page' : 'unit'}</strong>{' '}
                to <strong>{formatCurrency(nextRate)}/{quantityType === 'page' ? 'page' : 'unit'}</strong>.
              </span>
            </div>
          </div>
        );
      })()}

      <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded">
        <p className="text-sm text-amber-800">
          <strong>Need more than {maxQty.toLocaleString()} {unitLabel}s?</strong> Contact us at{' '}
          <a href="tel:9054470257" className="underline font-semibold hover:text-amber-900">
            (905) 447-0257
          </a>{' '}
          for volume pricing and custom quotes.
        </p>
      </div>

      {quantity && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded mt-4">
          <p className="text-sm text-green-800 flex items-center gap-2">
            <span className="inline-block w-2 h-2 bg-green-500 rounded-full"></span>
            <strong>
              Quantity selected: {quantity.toLocaleString()} {unitLabel}
              {quantity > 1 ? 's' : ''}
            </strong>{' '}
            - Click &quot;Continue&quot; below to proceed
          </p>
        </div>
      )}
    </div>
  );
};
